/**
 * RaiseFindingView -- operator manual intake (P347).
 *
 * Route: /twin/findings/new (admin). The observations no connector
 * carries: a branch outage phoned in, a third-party letter, a
 * near-miss. Intake, not workflow -- the raised Finding is first-class
 * (drives cascade/resilience/war-room) and human remediation state
 * lives on the CAP, per docs/incident-posture.md.
 */
import { useToast } from "shared/toast/ToastProvider";
import { ApiError } from "shared/api/client";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "shared/api/client";
import type { Finding } from "shared/api/types";
import { MarsadIcon } from "shared/components/MarsadIcon";

const SEVERITIES = ["critical", "high", "medium", "low"] as const;
const CATEGORIES = [
  "cyber",
  "technology",
  "third_party",
  "process",
  "people",
  "facility",
  "data",
  "other",
] as const;
// A6: these listed through /v1/admin/foundation/*, which is admin-only,
// so the Resilience Lead opening this form during an incident got an
// empty picker with no error -- the estate looked empty rather than
// forbidden. /v1/catalogue is the narrow read a picker needs.
const ENTITY_KINDS = [
  {
    value: "service",
    label: "Service",
    endpoint: "/v1/catalogue/service?limit=500",
  },
  {
    // A6: this sent "ibs" and the API refused it -- its canonical kind
    // is `ibs_service`, and the alias map knew only the harness's
    // plural "ibs_services". The submit failed with a 422 that read
    // like the reporter had filled the form in wrongly.
    value: "ibs_service",
    label: "Important business service",
    // Round 16 L4 -- the IBS register only; a function has its own kind.
    endpoint: "/v1/catalogue/ibs_service?limit=500&register=business",
  },
  {
    // A Critical Internal Function is an ibs_service row of the internal
    // class; it was offered under "Important business service".
    value: "ibs_service_internal",
    entityType: "ibs_service",
    label: "Critical internal function",
    endpoint: "/v1/catalogue/ibs_service?limit=500&register=function",
  },
  {
    value: "capability",
    label: "Capability",
    endpoint: "/v1/catalogue/capability?limit=500",
  },
  {
    value: "vendor",
    label: "Vendor",
    endpoint: "/v1/catalogue/vendor?limit=500",
  },
  {
    // Round 10 F1 -- thirty of the estate's sixty-seven breaching
    // architecture-standard rows are configuration items, and each of
    // them linked here. The kind is a valid finding subject and the
    // catalogue has always served it; the picker simply did not offer
    // it, so the form arrived naming a subject it could not show.
    value: "configuration_item",
    label: "Configuration item",
    endpoint: "/v1/catalogue/configuration_item?limit=500",
  },
] as const;

interface EntityOption {
  id: string;
  name: string;
  code: string | null;
}

export function RaiseFindingView(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<string>("high");
  const [category, setCategory] = useState<string>("technology");
  /*
   * Round 9 F1 -- arrive with the subject already named.
   *
   * A register that flags a service and offers "raise it as a finding"
   * is only useful if the form knows which service. Landing on an
   * empty form asks the operator to go and find again what they were
   * just looking at, which is the dead end this route was added to
   * remove.
   *
   * Seeded, not locked: this is a starting point, and somebody who
   * arrived by a link may still have meant a different subject.
   */
  const [entityKind, setEntityKind] = useState<string>(
    () => search.get("entity_kind") ?? "service",
  );
  const [entityId, setEntityId] = useState<string>(() => search.get("entity_id") ?? "");

  const kindDef = ENTITY_KINDS.find((k) => k.value === entityKind);
  const toast = useToast();
  const options = useQuery({
    queryKey: ["raise-finding", "options", entityKind],
    queryFn: () => api.get<EntityOption[]>(kindDef?.endpoint ?? ""),
    enabled: Boolean(kindDef),
    staleTime: 5 * 60_000,
  });

  // Round 18 G25 -- a slow answer must not read as a stuck button.
  const [slow, setSlow] = useState(false);
  const [opening, setOpening] = useState(false);
  const submit = useMutation({
    mutationFn: () =>
      api.post<Finding>("/v1/admin/findings/manual", {
        title,
        description: description || null,
        severity,
        category,
        entity_type:
          (kindDef && "entityType" in kindDef ? kindDef.entityType : undefined) ??
          entityKind,
        entity_id: entityId,
      }),
    onSuccess: (finding) => {
      setOpening(true);
      const on = options.data?.find((e) => e.id === entityId)?.name;
      toast.push({
        tone: "success",
        message: on
          ? t("screens.raiseFindingView.raisedOn", { name: on })
          : t("screens.raiseFindingView.raised"),
      });
      navigate(`/twin/findings/${finding.id}`);
    },
  });

  useEffect(() => {
    if (!submit.isPending) {
      setSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setSlow(true), 1000);
    return () => window.clearTimeout(timer);
  }, [submit.isPending]);

  const inputCls = "w-full rounded border border-slate-300 px-2 py-1.5 text-sm";

  return (
    <article data-testid="raise-finding-view" className="max-w-2xl space-y-4">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="finding"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("screens.raiseFindingView.raiseAFinding")}
        </h1>
        <p className="text-sm text-slate-500">
          {t("screens.raiseFindingView.manualIntakeForObservationsNoConnectorCarrie")}
        </p>
      </header>

      <div className="space-y-3 rounded-xl border border-paper-2 bg-white p-4 shadow-card">
        <label className="block text-sm">
          <span className="mb-1 block text-slate-600">
            {t("screens.raiseFindingView.title")}
          </span>
          <input
            dir="auto"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="raise-title"
            className={inputCls}
            maxLength={255}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-slate-600">
            {t("screens.raiseFindingView.description")}
          </span>
          <textarea
            dir="auto"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="raise-description"
            className={inputCls}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">
              {t("screens.raiseFindingView.severity")}
            </span>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              data-testid="raise-severity"
              className={inputCls}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">
              {t("screens.raiseFindingView.category")}
            </span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="raise-category"
              className={inputCls}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replace("_", " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">
              {t("screens.raiseFindingView.impactedEntityKind")}
            </span>
            <select
              value={entityKind}
              onChange={(e) => {
                setEntityKind(e.target.value);
                setEntityId("");
              }}
              data-testid="raise-entity-kind"
              className={inputCls}
            >
              {ENTITY_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">
              {t("screens.raiseFindingView.impactedEntity")}
            </span>
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              data-testid="raise-entity"
              className={inputCls}
              disabled={options.isLoading}
            >
              <option value="">{t("screens.raiseFindingView.select")}</option>
              {(options.data ?? []).map((o, _i, all) => (
                <option key={o.id} value={o.id}>
                  {/* Round 17 I16 -- the name a person knows; the key
                      only where two share a name and it is needed to
                      tell them apart. */}
                  {all.filter((x) => x.name === o.name).length > 1
                    ? `${o.name} (${o.code})`
                    : o.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {submit.isError && (
          <p data-testid="raise-error" className="text-sm text-rose-700">
            {/* A6: this said "check the fields" whatever had gone wrong,
                so a server-side refusal was reported as the reporter's
                mistake. Say what the server said. */}
            {submit.error instanceof ApiError
              ? submit.error.detail
              : t("screens.raiseFindingView.couldNotRaiseTheFindingCheckThe")}
          </p>
        )}
        <button
          type="button"
          onClick={() => submit.mutate()}
          disabled={!title || !entityId || submit.isPending || opening}
          data-testid="raise-submit"
          className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {opening
            ? t("screens.raiseFindingView.opening")
            : submit.isPending
              ? slow
                ? t("screens.raiseFindingView.stillSaving")
                : t("screens.raiseFindingView.raising")
              : t("screens.raiseFindingView.raiseFinding")}
        </button>
      </div>
    </article>
  );
}
